import { useEffect, useState, useCallback } from "react";
import { RiCloseFill, RiMenuFill } from "react-icons/ri";

import { cn } from "../utils/cn";
import { AGAPlaybookLogo } from "./aga-logo";

const navItems = [
  {
    title: "Affordable",
    url: "/affordable",
  },
  {
    title: "Reliable",
    url: "/reliable",
  },
  {
    title: "Safe",
    url: "/safe",
  },
  {
    title: "Environment",
    url: "/environment",
  },
  {
    title: "Innovation",
    url: "/innovation",
  },
  {
    title: "Natural Gas Data",
    url: "/natural-gas-data",
  },
];

const Navigation = ({
  title,
  currentURL,
}: {
  title: string;
  currentURL: string;
}) => {
  // Whether the side menu nav is open or not, hides on desktop-width screens
  const [nav, setNav] = useState(false);
  // Detects scroll direction of user to control hiding the header (visible state)
  const [scroll, setScroll] = useState(0);
  // Controls whether the header is visible, hides when the user scrolls down
  const [visible, setVisible] = useState(true);

  // useCallback hook to detect scroll direction, hides header if scroll direction is down
  const trackScroll = useCallback(() => {
    const windowGlobal = typeof window !== `undefined` && window;
    if (windowGlobal) {
      if (windowGlobal.scrollY >= 100 && windowGlobal.scrollY > scroll) {
        setVisible(false);
      } else {
        setVisible(true);
      }
      setScroll(windowGlobal.scrollY);
    }
  }, [scroll]);

  // useEffect adds the trackScroll function to the window as an event listener
  useEffect(() => {
    const windowGlobal = typeof window !== `undefined` && window;
    if (windowGlobal) {
      setScroll(windowGlobal.scrollY);
      windowGlobal.addEventListener(`scroll`, trackScroll);
    }

    return () => {
      if (windowGlobal) {
        windowGlobal.removeEventListener(`scroll`, trackScroll);
      }
    };
  }, [trackScroll]);

  return (
    <header
      className={cn(
        "fixed left-0 right-0 top-0 z-30 mx-auto w-full bg-white px-5 font-sans transition-all",
        visible ? "max-h-[1000px]" : "xl:max-h-0 xl:overflow-hidden xl:py-0",
      )}
    >
      <div className="container mx-auto xl:flex xl:items-center xl:justify-between xl:text-center 2xl:max-w-[1376px]">
        <div className="mx-auto flex items-center justify-between transition-all xl:mx-0 xl:block xl:w-auto xl:px-0 xl:pb-0">
          <a href="/" className="block w-full">
            <AGAPlaybookLogo className="w-48 py-2" />
            <h1 className="sr-only">{title}</h1>
          </a>
          <button
            className="z-40 cursor-pointer fill-teal-800 text-2xl focus:outline-none xl:hidden"
            onClick={() => setNav(!nav)}
            aria-label="Home"
            type="button"
          >
            {nav ? <RiCloseFill /> : <RiMenuFill />}
          </button>
        </div>
        <nav
          className={cn(
            "pointer-events-none absolute right-0 h-screen w-full translate-x-full bg-teal-800 py-8 transition duration-300 xl:pointer-events-auto xl:static xl:h-auto xl:translate-x-0 xl:bg-transparent xl:py-0 xl:opacity-100",
            nav &&
              "pointer-events-auto translate-x-1/4 md:translate-x-3/4 xl:translate-x-0",
          )}
        >
          <ul className="flex w-full flex-col items-start justify-center space-y-4 px-8 md:px-4 xl:flex xl:w-auto xl:flex-row xl:items-center xl:justify-between xl:space-y-0 xl:px-0 xl:pl-16 xl:text-xs 2xl:pl-36 2xl:text-base">
            {navItems.map((item, i) => (
              <li key={i}>
                <a
                  className={cn(
                    "text-sm font-bold uppercase text-white transition hover:text-royal-500 focus:text-royal-500 xl:text-grey-500 xl:hover:text-navy-800 xl:focus:text-navy-800",
                    currentURL !== "/" &&
                      item.url.includes(currentURL) &&
                      "underline underline-offset-2 xl:text-navy-800 xl:no-underline",
                  )}
                  href={item.url}
                  onClick={() => setNav(false)}
                >
                  {item.title}
                </a>
              </li>
            ))}
            <li>
              <a
                className={
                  "block transform rounded-[10px] bg-teal-500 px-7 py-1 text-sm font-bold uppercase text-white transition hover:scale-105 focus:scale-105"
                }
                href="/asset-library"
                onClick={() => setNav(false)}
              >
                Asset Library
              </a>
            </li>
          </ul>
        </nav>
      </div>
    </header>
  );
};

export default Navigation;
